'use strict';

import {TeejitRestClient} from '../services/teejit-rest-client.js';
import {TeejitSessionWarmup} from '../services/teejit-session-warmup.js';

class KnowledgeHubTour {
    constructor(config = {}) {
        this.config = config;
        this.messages = config.messages || {};
        this.steps = [];
        this.index = 0;
        this.target = null;
        this.card = null;
        this.spotlight = null;
        this.progress = null;
        this.remoteStateLoaded = false;
        this.remoteState = null;
        this.booted = false;
        this.repositionHandler = () => this.position();
        this.startTriggerHandler = (event) => this.handleStartTriggerClick(event);
        this.restClient = new TeejitRestClient({
            restBaseUrl: config.restBaseUrl || window.khThemeRuntime?.restBaseUrl,
            accessToken: config.accessToken || window.khThemeRuntime?.userAccessToken,
            nonce: config.nonce || window.khThemeRuntime?.restNonce,
        });
    }

    boot() {
        this.bindStartTrigger();

        if (this.booted) {
            return;
        }

        if (!this.isAllowedToRun()) {
            return;
        }

        if (!this.isAutoStartAllowed()) {
            return;
        }

        this.booted = true;
        this.waitForSessionWarmup()
            .then((payload) => this.applyRuntimeUserData(payload))
            .catch(() => null)
            .then(() => this.loadRemoteState())
            .catch(() => null)
            .finally(() => this.waitForAutoStartReadiness())
            .then(() => this.startIfNeeded());
    }

    updateConfig(config = {}) {
        const messages = {
            ...(this.config.messages || {}),
            ...(config.messages || {}),
        };

        this.config = {
            ...this.config,
            ...config,
            messages,
        };
        this.messages = messages;
        this.restClient = new TeejitRestClient({
            restBaseUrl: this.config.restBaseUrl || window.khThemeRuntime?.restBaseUrl,
            accessToken: this.config.accessToken || window.khThemeRuntime?.userAccessToken,
            nonce: this.config.nonce || window.khThemeRuntime?.restNonce,
        });
    }

    bindStartTrigger() {
        if (this.startTriggerBound) {
            return;
        }

        this.startTriggerBound = true;
        document.addEventListener('click', this.startTriggerHandler);
    }

    handleStartTriggerClick(event) {
        const trigger = event.target?.closest?.('[data-kh-tour-start]');
        if (!trigger) {
            return;
        }

        event.preventDefault();
        this.start(true);
    }

    isAllowedToRun() {
        const runtime = window.khThemeRuntime || {};
        return this.config.enabled === true
            && (
                this.config.isLoggedIn === true
                || runtime.isLoggedIn === true
                || !!(this.config.accessToken || runtime.userAccessToken)
            );
    }

    isAutoStartAllowed() {
        return this.config.autoStart === true
            && !this.isLoginOrRegistrationPage()
            && this.isKnowledgeHubHomePage();
    }

    isLoginOrRegistrationPage() {
        return !!document.getElementById('login-page')
            || !!document.querySelector('#t-loginform, #t-register, .t-login');
    }

    isKnowledgeHubHomePage() {
        return !!document.getElementById('page-teejit-home')
            && !!document.getElementById('kh-home-api-fragments-config');
    }

    waitForSessionWarmup() {
        if (!window.khThemeRuntime?.sessionNeedsWarmup) {
            return Promise.resolve(null);
        }

        return TeejitSessionWarmup.bootFromRuntime() || Promise.resolve(null);
    }

    applyRuntimeUserData(payload) {
        const userId = payload?.user_data?.id || window.khThemeRuntime?.userId;
        if (!userId) {
            return;
        }

        this.config.userId = String(userId);
    }

    startIfNeeded() {
        if (this.hasSeenTour()) {
            this.clearPendingRegistrationTour();
            return;
        }

        this.start(false);
    }

    start(force = false) {
        if (!this.isAllowedToRun()) {
            this.updateConfig(window.khThemeRuntime?.knowledgeHubTour || {});
            if (!this.isAllowedToRun()) {
                return;
            }
        }

        if (!force && this.hasSeenTour()) {
            this.clearPendingRegistrationTour();
            return;
        }

        this.steps = this.resolveSteps();
        if (!this.steps.length) {
            if (!force) {
                this.markTourSeen();
            }
            return;
        }

        this.index = 0;
        this.ensureElements();
        if (!force) {
            this.markTourSeen();
        }
        document.body.classList.add('kh-knowledgehub-tour-open');
        window.addEventListener('resize', this.repositionHandler);
        window.addEventListener('scroll', this.repositionHandler, true);
        this.showStep(0);
    }

    waitForAutoStartReadiness() {
        return this.waitForHomeHydratorReady()
            .then(() => this.waitForLayoutIdle());
    }

    waitForHomeHydratorReady() {
        if (!document.getElementById('kh-home-api-fragments-config') || window.khHomeHydratorInitialReady === true) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            let resolved = false;
            const finish = () => {
                if (resolved) {
                    return;
                }
                resolved = true;
                window.removeEventListener('kh:homeHydratorReady', finish);
                resolve();
            };

            window.addEventListener('kh:homeHydratorReady', finish, {once: true});
            window.setTimeout(finish, 6500);
        });
    }

    waitForLayoutIdle() {
        const body = document.body;
        if (!body || !window.MutationObserver) {
            return new Promise((resolve) => window.setTimeout(resolve, 900));
        }

        return new Promise((resolve) => {
            let idleTimer = null;
            let done = false;
            const observer = new MutationObserver(() => scheduleFinish());
            const finish = () => {
                if (done) {
                    return;
                }
                done = true;
                window.clearTimeout(idleTimer);
                window.clearTimeout(maxTimer);
                observer.disconnect();
                resolve();
            };
            const scheduleFinish = () => {
                if (done) {
                    return;
                }
                window.clearTimeout(idleTimer);
                idleTimer = window.setTimeout(finish, 900);
            };
            const maxTimer = window.setTimeout(finish, 4500);

            observer.observe(body, {
                childList: true,
                subtree: true,
            });
            scheduleFinish();
        });
    }

    resolveSteps() {
        return this.getStepDefinitions()
            .map((definition) => ({
                ...definition,
                target: this.findTarget(definition.selectors),
            }))
            .filter((step) => step.target);
    }

    getStepDefinitions() {
        return [
            {
                key: 'welcome',
                icon: 'waving_hand',
                selectors: ['#t-content-wrapper', '#page-profile', '.kh-app-page', '.wrapper'],
            },
            {
                key: 'topNavigation',
                icon: 'apps',
                selectors: ['.t-nav-head-apps', '.t-nav-head-mobile-toggle'],
            },
            {
                key: 'home',
                icon: 'home',
                selectors: ['[data-kh-tour="nav-home"]', '[data-kh-tour="left-home"]'],
            },
            {
                key: 'markedCourses',
                icon: 'bookmark_added',
                selectors: ['[data-kh-tour="nav-marked_courses"]', '[data-kh-tour="left-marked_courses"]'],
            },
            {
                key: 'events',
                icon: 'event',
                selectors: ['[data-kh-tour="nav-events"]', '[data-kh-tour="left-events"]'],
            },
            {
                key: 'certificates',
                icon: 'license',
                selectors: ['[data-kh-tour="nav-certificates"]', '[data-kh-tour="left-certificates"]'],
            },
            {
                key: 'innohub',
                icon: 'rocket_launch',
                selectors: ['[data-kh-tour="nav-innohub"]', '[data-kh-tour="left-innohub"]'],
            },
            {
                key: 'channels',
                icon: 'groups',
                selectors: ['[data-kh-tour="nav-channels"]', '[data-kh-tour="left-channels"]'],
            },
            {
                key: 'myChannels',
                icon: 'explore',
                selectors: ['[data-kh-tour="my-channels"]', '#t-nav-left-team-list'],
            },
            {
                key: 'search',
                icon: 'search',
                selectors: ['[data-kh-tour="search"]', '.t-kh-home-search-wrapper', '#t-searchform'],
            },
            {
                key: 'homepageBuilder',
                icon: 'dashboard_customize',
                selectors: ['#kh-home-builder .kh-home-builder-toggle'],
            },
            {
                key: 'profile',
                icon: 'person',
                selectors: ['[data-kh-tour="profile"]', '[data-kh-tour-start]'],
            },
        ];
    }

    findTarget(selectors = []) {
        for (const selector of selectors) {
            const candidates = Array.from(document.querySelectorAll(selector));
            const visible = candidates.find((element) => this.isVisible(element));
            if (visible) {
                return visible;
            }
        }

        return null;
    }

    isVisible(element) {
        if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true') {
            return false;
        }

        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity || '1') <= 0.05) {
            return false;
        }

        const rect = element.getBoundingClientRect();
        return rect.width > 8 && rect.height > 8;
    }

    ensureElements() {
        if (this.card && this.spotlight) {
            return;
        }

        this.spotlight = document.createElement('div');
        this.spotlight.className = 'kh-knowledgehub-tour-spotlight';

        this.card = document.createElement('section');
        this.card.className = 'kh-knowledgehub-tour-card';
        this.card.setAttribute('role', 'dialog');
        this.card.setAttribute('aria-live', 'polite');

        document.body.append(this.spotlight, this.card);
    }

    showStep(index) {
        if (index < 0) {
            return;
        }
        if (index >= this.steps.length) {
            this.finish(true);
            return;
        }

        this.target?.classList.remove('kh-knowledgehub-tour-target');
        this.index = index;
        this.target = this.steps[this.index].target;
        this.target.classList.add('kh-knowledgehub-tour-target');
        this.scrollTargetIntoView();
        this.renderCard();
        this.position();
        window.setTimeout(() => this.position(), 320);
    }

    scrollTargetIntoView() {
        // The welcome step highlights the whole page without changing the user's scroll position.
        if (this.steps[this.index].key === 'welcome') {
            return;
        }

        const rect = this.target.getBoundingClientRect();
        // Oversized targets only need to intersect the viewport; smaller ones should fit entirely.
        const verticallyVisible = rect.height > window.innerHeight
            ? rect.top < window.innerHeight && rect.bottom > 0
            : rect.top >= 0 && rect.bottom <= window.innerHeight;
        const horizontallyVisible = rect.width > window.innerWidth
            ? rect.left < window.innerWidth && rect.right > 0
            : rect.left >= 0 && rect.right <= window.innerWidth;

        if (!verticallyVisible || !horizontallyVisible) {
            this.target.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'nearest'});
        }
    }

    renderCard() {
        const step = this.steps[this.index];
        const title = this.message(step.key + 'Title', step.key);
        const text = this.message(step.key + 'Text', '');

        this.card.innerHTML = '';

        const eyebrow = document.createElement('span');
        eyebrow.className = 'kh-knowledgehub-tour-eyebrow';
        eyebrow.textContent = this.formatStepLabel();

        const icon = document.createElement('span');
        icon.className = 'kh-knowledgehub-tour-icon material-symbols-outlined';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = step.icon || 'tips_and_updates';

        const headline = document.createElement('h2');
        headline.textContent = title;

        const copy = document.createElement('p');
        copy.textContent = text;

        const progress = document.createElement('div');
        progress.className = 'kh-knowledgehub-tour-progress';
        const progressFill = document.createElement('span');
        progressFill.style.width = Math.round(((this.index + 1) / this.steps.length) * 100) + '%';
        progress.append(progressFill);

        const actions = document.createElement('div');
        actions.className = 'kh-knowledgehub-tour-actions';

        const skipButton = this.createButton(this.message('skip', 'Skip'), 'kh-knowledgehub-tour-link', () => this.finish(true));
        const previousButton = this.createButton(this.message('previous', 'Back'), 'kh-knowledgehub-tour-secondary', () => this.showStep(this.index - 1));
        previousButton.disabled = this.index === 0;

        const nextButtonLabel = this.index >= this.steps.length - 1
            ? this.message('finish', 'Done')
            : this.message('next', 'Next');
        const nextButton = this.createButton(nextButtonLabel, 'kh-knowledgehub-tour-primary', () => this.showStep(this.index + 1));

        actions.append(skipButton, previousButton, nextButton);
        this.card.append(eyebrow, icon, headline, copy, progress, actions);
    }

    createButton(label, className, handler) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = className;
        button.textContent = label;
        button.addEventListener('click', handler);
        return button;
    }

    position() {
        if (!this.card || !this.spotlight || !this.target) {
            return;
        }

        const rect = this.target.getBoundingClientRect();
        const cardRect = this.card.getBoundingClientRect();
        const padding = 12;
        const viewportGap = 16;

        const spotlightLeft = Math.max(8, rect.left - padding);
        const spotlightTop = Math.max(8, rect.top - padding);
        const spotlightWidth = Math.min(window.innerWidth - spotlightLeft - 8, rect.width + padding * 2);
        const spotlightHeight = Math.min(window.innerHeight - spotlightTop - 8, rect.height + padding * 2);

        this.spotlight.style.left = spotlightLeft + 'px';
        this.spotlight.style.top = spotlightTop + 'px';
        this.spotlight.style.width = spotlightWidth + 'px';
        this.spotlight.style.height = spotlightHeight + 'px';

        const canPlaceRight = rect.right + cardRect.width + 24 < window.innerWidth;
        const canPlaceLeft = rect.left - cardRect.width - 24 > 0;
        const canPlaceBelow = rect.bottom + cardRect.height + 24 < window.innerHeight;

        let left = canPlaceRight ? rect.right + 18 : (canPlaceLeft ? rect.left - cardRect.width - 18 : rect.left);
        let top = canPlaceBelow ? rect.bottom + 18 : rect.top - cardRect.height - 18;

        if (!canPlaceRight && !canPlaceLeft) {
            left = Math.min(window.innerWidth - cardRect.width - viewportGap, Math.max(viewportGap, rect.left));
        }

        left = Math.max(viewportGap, Math.min(left, window.innerWidth - cardRect.width - viewportGap));
        top = Math.max(viewportGap, Math.min(top, window.innerHeight - cardRect.height - viewportGap));

        this.card.style.left = left + 'px';
        this.card.style.top = top + 'px';
    }

    finish(markSeen) {
        if (markSeen) {
            this.markTourSeen();
        }

        window.removeEventListener('resize', this.repositionHandler);
        window.removeEventListener('scroll', this.repositionHandler, true);
        document.body.classList.remove('kh-knowledgehub-tour-open');
        this.target?.classList.remove('kh-knowledgehub-tour-target');
        this.target = null;
        this.card?.remove();
        this.spotlight?.remove();
        this.card = null;
        this.spotlight = null;
    }

    getStorageKey() {
        return 'khKnowledgeHubTourSeen:' + (this.config.storageVersion || 'v1') + ':' + this.getStorageUserId();
    }

    getPendingStorageKey() {
        return 'khKnowledgeHubTourPendingRegistration:' + (this.config.storageVersion || 'v1') + ':' + this.getStorageUserId();
    }

    getStorageUserId() {
        return String(this.config.userId || window.khThemeRuntime?.userId || 'current');
    }

    hasSeenTour() {
        if (this.remoteState?.seen === true) {
            return true;
        }

        try {
            return window.localStorage.getItem(this.getStorageKey()) === '1';
        } catch (error) {
            return true;
        }
    }

    hasPendingRegistrationTour() {
        try {
            return window.localStorage.getItem(this.getPendingStorageKey()) === '1'
                || window.localStorage.getItem('khKnowledgeHubTourPendingRegistration') === '1';
        } catch (error) {
            return false;
        }
    }

    clearPendingRegistrationTour() {
        try {
            window.localStorage.removeItem(this.getPendingStorageKey());
            window.localStorage.removeItem('khKnowledgeHubTourPendingRegistration');
        } catch (error) {
        }
    }

    markTourSeen() {
        try {
            window.localStorage.setItem(this.getStorageKey(), '1');
            this.clearPendingRegistrationTour();
        } catch (error) {
        }

        this.remoteState = {
            seen: true,
            completed_at: new Date().toISOString(),
        };
        this.saveRemoteState();
    }

    async loadRemoteState() {
        if (!this.restClient.accessToken) {
            return null;
        }

        const payload = await this.restClient.requestJson('user/homepage-settings');
        const state = payload?.homepage?.tours?.knowledgehub;
        this.remoteStateLoaded = true;

        if (state && typeof state === 'object') {
            this.remoteState = {
                seen: state.seen === true,
                completed_at: typeof state.completed_at === 'string' ? state.completed_at : null,
            };

            if (this.remoteState.seen) {
                try {
                    window.localStorage.setItem(this.getStorageKey(), '1');
                    this.clearPendingRegistrationTour();
                } catch (error) {
                }
                return this.remoteState;
            }
        }

        if (this.hasSeenTour()) {
            this.saveRemoteState();
        }

        return this.remoteState;
    }

    async saveRemoteState() {
        if (!this.restClient.accessToken) {
            return;
        }

        try {
            await this.restClient.requestJson('user/homepage-settings', {
                method: 'POST',
                isJson: true,
                body: {
                    homepage: {
                        tours: {
                            knowledgehub: {
                                seen: true,
                                completed_at: this.remoteState?.completed_at || new Date().toISOString(),
                            },
                        },
                    },
                },
            });
        } catch (error) {
            if (window.khThemeRuntime?.debugAjax) {
                console.error('KnowledgeHub tour state could not be saved.', error);
            }
        }
    }

    message(key, fallback) {
        return this.messages[key] || fallback;
    }

    formatStepLabel() {
        const template = this.message('stepOf', 'Step %1$s of %2$s');
        return String(template).replace('%1$s', String(this.index + 1)).replace('%2$s', String(this.steps.length));
    }
}

const bootKnowledgeHubTour = () => {
    const runtime = window.khThemeRuntime || {};
    if (window.khKnowledgeHubTour instanceof KnowledgeHubTour) {
        window.khKnowledgeHubTour.updateConfig(runtime.knowledgeHubTour || {});
        window.khKnowledgeHubTour.boot();
        return window.khKnowledgeHubTour;
    }

    window.khKnowledgeHubTour = new KnowledgeHubTour(runtime.knowledgeHubTour || {});
    window.khKnowledgeHubTour.boot();
    return window.khKnowledgeHubTour;
};

if (typeof window !== 'undefined') {
    window.KnowledgeHubTour = KnowledgeHubTour;
    window.khBootKnowledgeHubTour = bootKnowledgeHubTour;

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bootKnowledgeHubTour, {once: true});
    } else {
        bootKnowledgeHubTour();
    }
}
